import { useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useParams, useNavigate } from "react-router-dom";
import {
  Camera, Check, X, AlertTriangle, ArrowLeft, Ticket, User, Clock,
  QrCode, RotateCcw,
} from "lucide-react";

export default function ScannerPage() {
  const { eventId } = useParams();
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const scannerRef = useRef(null);
  const html5QrRef = useRef(null);
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [eventInfo, setEventInfo] = useState(null);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    const fetchInfo = async () => {
      try {
        const res = await api.get(`/tickets/events/${eventId}/sales`);
        setEventInfo(res.data.event);
        setStats(res.data.stats);
      } catch {
        navigate("/events");
      }
    };
    fetchInfo();
  }, [api, eventId, navigate]);

  const startScanner = useCallback(async () => {
    try {
      const { Html5Qrcode } = await import("html5-qrcode");
      if (html5QrRef.current) {
        await html5QrRef.current.stop().catch(() => {});
      }
      const scanner = new Html5Qrcode("qr-reader");
      html5QrRef.current = scanner;
      setScanning(true);
      setResult(null);

      await scanner.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 250, height: 250 } },
        async (decodedText) => {
          await scanner.stop().catch(() => {});
          setScanning(false);
          await validateTicket(decodedText);
        },
        () => {}
      );
    } catch (err) {
      setScanning(false);
      setResult({
        valid: false,
        message: "Impossible d'acceder a la camera. Verifie les permissions.",
        reason: "camera_error",
      });
    }
  }, []);

  const validateTicket = async (qrData) => {
    setLoading(true);
    try {
      // Parse QR data: RF:{ticket_id}:{signature}
      const parts = qrData.split(":");
      if (parts.length !== 3 || parts[0] !== "RF") {
        setResult({ valid: false, message: "QR code invalide - ce n'est pas un billet ReallyFan", reason: "invalid_format" });
        setLoading(false);
        return;
      }

      const [, ticketId, signature] = parts;
      const res = await api.post("/tickets/validate", { ticket_id: ticketId, signature });
      setResult(res.data);

      // Refresh stats
      const statsRes = await api.get(`/tickets/events/${eventId}/sales`);
      setStats(statsRes.data.stats);
    } catch (err) {
      setResult({
        valid: false,
        message: err.response?.data?.detail || "Erreur de validation",
        reason: "error",
      });
    }
    setLoading(false);
  };

  useEffect(() => {
    return () => {
      if (html5QrRef.current) {
        html5QrRef.current.stop().catch(() => {});
      }
    };
  }, []);

  return (
    <div className="py-6 space-y-6" data-testid="scanner-page">
      <button onClick={() => navigate(`/events/${eventId}`)} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="w-4 h-4" /> Retour
      </button>

      <div>
        <h1 className="font-heading font-extrabold text-2xl text-foreground flex items-center gap-2">
          <QrCode className="w-7 h-7 text-[#FF6B35]" />
          Scanner de billets
        </h1>
        {eventInfo && <p className="text-sm text-muted-foreground mt-1">{eventInfo.title}</p>}
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-3 gap-3" data-testid="scanner-stats">
          <div className="bg-card border border-border rounded-2xl p-3 text-center">
            <p className="font-heading font-extrabold text-xl text-[#FF6B35]">{stats.scanned}</p>
            <p className="text-[10px] text-muted-foreground">Scannes</p>
          </div>
          <div className="bg-card border border-border rounded-2xl p-3 text-center">
            <p className="font-heading font-extrabold text-xl text-emerald-500">{stats.valid_remaining}</p>
            <p className="text-[10px] text-muted-foreground">Restants</p>
          </div>
          <div className="bg-card border border-border rounded-2xl p-3 text-center">
            <p className="font-heading font-extrabold text-xl text-foreground">{stats.total_sold}</p>
            <p className="text-[10px] text-muted-foreground">Vendus</p>
          </div>
        </div>
      )}

      {/* Scanner area */}
      <div className="bg-card border border-border rounded-3xl p-4 space-y-4">
        <div
          id="qr-reader"
          ref={scannerRef}
          className="w-full rounded-2xl overflow-hidden bg-black/10 min-h-[280px]"
          data-testid="qr-reader"
        />

        {!scanning && !result && (
          <button
            onClick={startScanner}
            className="w-full bg-[#FF6B35] text-white font-bold py-3.5 rounded-full text-sm hover:scale-[1.02] active:scale-[0.98] transition-transform flex items-center justify-center gap-2"
            data-testid="start-scanner-btn"
          >
            <Camera className="w-5 h-5" />
            Ouvrir la camera
          </button>
        )}

        {scanning && (
          <p className="text-center text-sm text-muted-foreground animate-pulse">
            Scan en cours... Pointez vers le QR code
          </p>
        )}

        {loading && (
          <div className="text-center py-4">
            <div className="w-8 h-8 border-2 border-[#FF6B35] border-t-transparent rounded-full animate-spin mx-auto" />
            <p className="text-sm text-muted-foreground mt-2">Verification...</p>
          </div>
        )}

        {/* Result */}
        {result && !loading && (
          <div
            className={`rounded-2xl p-5 text-center space-y-3 ${
              result.valid
                ? "bg-emerald-500/10 border border-emerald-500/30"
                : "bg-red-500/10 border border-red-500/30"
            }`}
            data-testid="scan-result"
          >
            <div className={`w-16 h-16 rounded-full mx-auto flex items-center justify-center ${
              result.valid ? "bg-emerald-500" : "bg-red-500"
            }`}>
              {result.valid ? <Check className="w-8 h-8 text-white" /> : <X className="w-8 h-8 text-white" />}
            </div>

            <p className={`font-heading font-bold text-lg ${result.valid ? "text-emerald-500" : "text-red-400"}`}>
              {result.valid ? "BILLET VALIDE" : "BILLET INVALIDE"}
            </p>
            <p className="text-sm text-foreground/70">{result.message}</p>

            {result.ticket && (
              <div className="text-xs text-muted-foreground space-y-1 pt-2 border-t border-border">
                <p className="flex items-center justify-center gap-1">
                  <User className="w-3 h-3" /> {result.ticket.buyer_pseudo}
                </p>
                <p className="flex items-center justify-center gap-1">
                  <Ticket className="w-3 h-3" /> {result.ticket.category_name}
                </p>
                {result.ticket.scanned_at && (
                  <p className="flex items-center justify-center gap-1">
                    <Clock className="w-3 h-3" /> Scanne a {new Date(result.ticket.scanned_at).toLocaleTimeString("fr-FR")}
                  </p>
                )}
              </div>
            )}

            <button
              onClick={() => { setResult(null); startScanner(); }}
              className="bg-[#FF6B35] text-white font-bold px-6 py-2.5 rounded-full text-sm hover:scale-105 active:scale-95 transition-transform flex items-center justify-center gap-2 mx-auto"
              data-testid="scan-again-btn"
            >
              <RotateCcw className="w-4 h-4" />
              Scanner un autre billet
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
